import React from "react";
import { LogoutOutlined } from "@ant-design/icons";
import { Drawer, Empty, Modal, notification,Space, Table as AntTable, Tag, Tooltip, Typography } from "antd";
import moment from "moment";
import styled from "styled-components";

import api from "../../api";
import { Button } from "../../components/actions/button";
import { usePermission } from "../../hooks";
import { useAppData } from "../../store/app-data";

const { Title, Text } = Typography;

const CHANNEL_LABELS = {
  mobile: { label: "Моб. приложение", color: "geekblue" },
  site: { label: "Сайт", color: "green" },
};

const CONFIRM_TEXT =
    "Вы уверены, что хотите завершить все активные сессии пользователя? " +
    "Пользователь будет вынужден авторизоваться заново на всех устройствах.";

// Лёгкое форматирование User-Agent (парсинг намеренно на фронте, серверный сырой UA отдаётся как есть).
function formatUserAgent(ua) {
  if (!ua) {
    return null;
  }

  const os =
        /iphone|ipad|ipod/i.test(ua) ? "iOS" :
          /android/i.test(ua) ? "Android" :
            /windows/i.test(ua) ? "Windows" :
              /mac os|macintosh/i.test(ua) ? "macOS" :
                /linux/i.test(ua) ? "Linux" : null;

  const browser =
        /edg\//i.test(ua) ? "Edge" :
          /chrome|crios/i.test(ua) ? "Chrome" :
            /firefox|fxios/i.test(ua) ? "Firefox" :
              /safari/i.test(ua) ? "Safari" : null;

  const parts = [os, browser].filter(Boolean);

  return parts.length ? parts.join(" · ") : null;
}

function formatDate(value) {
  return value ? moment(value).format("DD.MM.YYYY HH:mm") : "—";
}

export function SessionsDrawer({ title, userId, onClose }) {
  const layoutConfig = useAppData((state) => state.layoutConfig);
  const { hasPermission } = usePermission();
  const canTerminate = hasPermission("clients-sessions.terminate");

  const [sessions, setSessions] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    if (userId) {
      loadSessions();
    }
  }, [userId]);

  async function loadSessions() {
    setIsLoading(true);
    try {
      const { data } = await api.get(`users/${userId}/sessions`);
      setSessions(data?.sessions ?? []);
    } catch (error) {
      notification.error({ message: "Не удалось получить активные сессии" });
    } finally {
      setIsLoading(false);
    }
  }

  async function terminateAll() {
    try {
      await api.post(`users/${userId}/sessions/terminate`);
      notification.success({ message: "Все сессии пользователя завершены" });
      await loadSessions();
    } catch (error) {
      notification.error({
        message: error?.response?.data?.message ?? "Не удалось завершить сессии",
      });
      throw error;
    }
  }

  function handleTerminate() {
    Modal.confirm({
      title: "Выйти со всех устройств?",
      content: CONFIRM_TEXT,
      okText: "Да, завершить",
      okType: "danger",
      cancelText: "Отмена",
      centered: true,
      maskClosable: false,
      onOk: () => terminateAll(),
    });
  }

  const columns = [
    {
      title: "Канал",
      dataIndex: "channel",
      key: "channel",
      width: 150,
      render: (channel) => {
        const meta = CHANNEL_LABELS[channel] ?? { label: channel, color: "default" };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "Устройство",
      key: "device",
      render: (_, record) => {
        const friendly = record.deviceName || formatUserAgent(record.userAgent);
        return (
          <Space direction="vertical" size={0}>
            <Text>{friendly ?? "Неизвестное устройство"}</Text>
            {record.userAgent && (
              <Tooltip title={record.userAgent}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.userAgent.length > 60
                    ? `${record.userAgent.slice(0, 60)}…`
                    : record.userAgent}
                </Text>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    {
      title: "IP",
      dataIndex: "ip",
      key: "ip",
      width: 140,
      render: (ip) => ip ?? "—",
    },
    {
      title: "Последняя активность",
      dataIndex: "lastActiveAt",
      key: "lastActiveAt",
      width: 170,
      render: formatDate,
    },
    {
      title: "Вход",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: formatDate,
    },
  ];

  return (
    <DrawerEdit
      width={layoutConfig.isMobile ? "90%" : "60%"}
      title={title}
      onClose={onClose}
      open
    >
      <HeaderRow>
        <Title level={5} style={{ margin: 0 }}>
                    Активные сессии
        </Title>
        {canTerminate && (
          <Button
            danger
            icon={<LogoutOutlined />}
            disabled={isLoading || sessions.length === 0}
            onClick={handleTerminate}
          >
                        Выйти со всех устройств
          </Button>
        )}
      </HeaderRow>

      {(!isLoading && sessions.length === 0) ? (
        <Empty description="У пользователя нет активных сессий" />
      ) : (
        <StyledTable
          rowKey="id"
          dataSource={sessions}
          columns={columns}
          loading={isLoading}
          pagination={false}
        />
      )}
    </DrawerEdit>
  );
}

const DrawerEdit = styled(Drawer)``;

const HeaderRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    gap: 12px;
`;

const StyledTable = styled(AntTable)`
    .ant-table-row {
        background: #ffffff;
    }
`;
